// ---------------------------------------------------------------------------
// Brand fonts — on-demand font loading for previews (shared client util)
// ---------------------------------------------------------------------------
// Lifted out of brand-design-system-tab.tsx so the LP Express per-block font
// picker can reuse the exact same preview-loading logic. Parameterized by a
// target Document so a preview rendered inside the mobile-preview iframe loads
// its fonts into the iframe's own document (its <head> / FontFace registry),
// not the top window's.
import { googleFontsCssUrl } from "@shared/google-fonts";
import { uploadedBrandFontFamily } from "@shared/brand-fonts";
import type { BrandFont } from "@shared/schema";

// Google Font families whose stylesheet <link> has already been injected, and
// uploaded fonts already registered via the FontFace API — tracked PER document
// so the top window and each iframe each get exactly one load.
const loadedGoogleByDoc = new WeakMap<Document, Set<string>>();
const loadedUploadByDoc = new WeakMap<Document, Set<string>>();

function docSet(map: WeakMap<Document, Set<string>>, doc: Document): Set<string> {
  let set = map.get(doc);
  if (!set) {
    set = new Set<string>();
    map.set(doc, set);
  }
  return set;
}

/** Inject a Google Fonts stylesheet <link> for any families not yet loaded in
 *  `doc`. Batches all missing families into a single request. */
export function ensureGoogleFontsLoaded(
  families: string[],
  doc: Document = document,
): void {
  const set = docSet(loadedGoogleByDoc, doc);
  const missing = families.filter((f) => f && !set.has(f));
  if (missing.length === 0) return;
  missing.forEach((f) => set.add(f));
  const link = doc.createElement("link");
  link.rel = "stylesheet";
  link.href = googleFontsCssUrl(missing);
  doc.head.appendChild(link);
}

/** Register an uploaded font via the FontFace API in `doc`, keyed by font id
 *  (the CSS family name is derived from the id, never the display name, so two
 *  uploads with the same file name never collide). Returns the CSS family. */
export function ensureUploadedFontLoaded(
  id: string,
  url: string,
  doc: Document = document,
): string {
  const cssFamily = uploadedBrandFontFamily({ id });
  const set = docSet(loadedUploadByDoc, doc);
  if (set.has(cssFamily)) return cssFamily;
  set.add(cssFamily);
  try {
    // Uploaded fonts live in R2, which sends no CORS headers. Browsers always
    // fetch fonts in CORS mode, so load through our same-origin font proxy.
    const src = /^https?:\/\//i.test(url)
      ? `/api/brand-font?url=${encodeURIComponent(url)}`
      : url;
    const win = doc.defaultView ?? (typeof window !== "undefined" ? window : null);
    const FontFaceCtor: typeof FontFace | undefined =
      (win as any)?.FontFace ?? (typeof FontFace !== "undefined" ? FontFace : undefined);
    if (FontFaceCtor && doc.fonts) {
      const face = new FontFaceCtor(cssFamily, `url("${src}")`);
      face
        .load()
        .then((f) => doc.fonts.add(f))
        .catch(() => {});
    }
  } catch {
    // FontFace unsupported / bad URL — preview falls back to the default font.
  }
  return cssFamily;
}

/** CSS font-family used to PREVIEW a brand font entry, loading it on demand. */
export function previewFamilyFor(font: BrandFont, doc: Document = document): string {
  if (font.source === "google") {
    ensureGoogleFontsLoaded([font.family], doc);
    return `'${font.family}', sans-serif`;
  }
  if (font.url) {
    const cssFamily = ensureUploadedFontLoaded(font.id, font.url, doc);
    return `'${cssFamily}', sans-serif`;
  }
  return "inherit";
}
